import { CdkMenuModule } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  AppPageBase,
  AuthService,
  EnumRouteLinkFull,
  LANG_OPTIONS,
  LanguageService,
  ThemeMode,
  ThemeService,
  UserAvatarComponent,
} from '@lyricstream/shared';

/** Lien de navigation proposé dans le menu quand l'en-tête est trop étroit pour afficher les siens. */
export interface AccountMenuNavItem {
  /** Clé de traduction du libellé ; `null` pour un lien personnalisé (on affiche alors `label`). */
  key: string | null;
  label: string;
  /** Segments de route sans la langue (la langue est ajoutée par le menu). */
  commands: string[];
}

/**
 * Menu du compte : pastille « ☰ + avatar » (visiteur : ☰ + silhouette) — CDK Menu, même principe que
 * lyric-toolbar / le menu par ligne audio de lyric-reader. Ouverture/fermeture/clic-extérieur/clavier
 * sont gérés par CdkMenuModule. Il regroupe la navigation (≤ 1024 px), le compte, le thème et la langue.
 */
@Component({
  selector: 'ls-avatar-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UserAvatarComponent, RouterLink, CdkMenuModule, TranslateModule],
  templateUrl: './avatar-menu.html',
  styleUrl: './avatar-menu.scss',
})
export class AvatarMenu extends AppPageBase {
  protected readonly authService = inject(AuthService);
  protected readonly themeService = inject(ThemeService);
  protected readonly langService = inject(LanguageService);
  routeNameFull = EnumRouteLinkFull;

  readonly isFullscreen = input(false);
  readonly showFullWidthBtn = input(false);
  /** Liens de navigation du site, proposés dans le menu (masqués au-delà de 1024 px où l'en-tête les affiche). */
  readonly navItems = input<AccountMenuNavItem[]>([]);
  readonly toggleFullscreen = output<void>();
  /** Visiteur : l'utilisateur demande à se connecter (le parent ouvre le popup de connexion). */
  readonly loginRequested = output<void>();

  protected readonly langOptions = LANG_OPTIONS;
  protected readonly themeModes: Array<{ mode: ThemeMode; icon: string; labelKey: string }> = [
    { mode: 'light', icon: 'ls-sun', labelKey: 'AVATAR_MENU.THEME_LIGHT' },
    { mode: 'dark', icon: 'ls-moon', labelKey: 'AVATAR_MENU.THEME_DARK' },
    { mode: 'auto', icon: 'ls-sun-moon', labelKey: 'AVATAR_MENU.THEME_AUTO' },
  ];

  // Ancré au bord droit du bouton (le menu était collé à droite via right:0 avant CDK) —
  // avec repli vers le bord gauche si l'espace manque à droite de l'écran.
  protected readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  ];

  /** Le sous-menu des langues s'ouvre à gauche de la ligne (le menu est collé au bord droit), sinon à droite. */
  protected readonly subPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -4 },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 4 },
  ];

  protected onToggleFullscreen(): void {
    this.toggleFullscreen.emit();
  }

  protected logout(): void {
    this.authService.logout();
  }
}
